// ABDM (Ayushman Bharat Digital Mission) PHR Gateway · stub.
// Fork 9 (health) owns the live FHIR R4 wiring. Here we ship just the
// model + ABHA ID validator so the integrating app can collect a valid
// ABHA in a nominee or beneficiary entry without waiting on the
// real handshake.

import type { ConnectorAdapter, ConnectorMode, ConnectorResult, ConnectorStatus } from './_types.ts';
import { err, nowIso, ok } from './_types.ts';

export type AbhaIdShape = '14digit' | 'address'; // ABHA Number vs ABHA Address

export interface AbhaIdInfo {
  readonly raw: string;
  readonly shape: AbhaIdShape;
  readonly normalised: string;
  readonly status: 'valid' | 'invalid' | 'structural';
}

export interface AbdmStubStatus extends ConnectorStatus {
  readonly id: 'abdm-phr-stub';
  readonly liveWiringOwner: 'fork-9';
}

export class AbdmPhrStubAdapter
  implements ConnectorAdapter<AbdmStubStatus, AbhaIdInfo>
{
  readonly id = 'abdm-phr-stub';
  readonly displayName = 'ABHA · ABDM PHR';
  readonly displayNameHi = 'आभा · एबीडीएम';
  readonly dpdpCategory = 'health-abha-identity';
  readonly mode: ConnectorMode;
  #pending: string | null = null;

  constructor(mode: ConnectorMode = 'test') {
    this.mode = mode;
  }

  async getStatus(): Promise<AbdmStubStatus> {
    return {
      id: this.id,
      mode: this.mode,
      connected: false,
      lastSyncAt: null,
      principalIdHash: null,
      liveWiringOwner: 'fork-9',
      notes: 'Stub. ABHA-ID validator only. Live ABDM FHIR R4 handshake owned by fork-9 health connectors.',
    };
  }

  async connect(): Promise<ConnectorResult<AbdmStubStatus>> {
    return ok(await this.getStatus());
  }

  async disconnect(): Promise<void> {
    this.#pending = null;
  }

  queueAbha(input: string): void {
    this.#pending = input;
  }

  async importPayload(): Promise<ConnectorResult<AbhaIdInfo>> {
    if (!this.#pending) {
      return err({ code: 'not-configured', message: 'No ABHA id queued.', retriable: false });
    }
    return ok(validateAbhaId(this.#pending));
  }

  mockPayload(): AbhaIdInfo {
    return { raw: '12345678901234', shape: '14digit', normalised: '12-3456-7890-1234', status: 'valid' };
  }
}

const ABHA_14_RE = /^\d{14}$/;
const ABHA_FORMATTED_RE = /^\d{2}-\d{4}-\d{4}-\d{4}$/;
const ABHA_ADDRESS_RE = /^[a-zA-Z][a-zA-Z0-9._]{2,17}@(abdm|sbx)$/;

export function validateAbhaId(input: string): AbhaIdInfo {
  const trimmed = input.trim();
  const digits = trimmed.replace(/[^\d]/g, '');
  if (ABHA_14_RE.test(digits)) {
    return {
      raw: input,
      shape: '14digit',
      normalised: `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}-${digits.slice(10, 14)}`,
      status: 'valid',
    };
  }
  if (ABHA_FORMATTED_RE.test(trimmed)) {
    return { raw: input, shape: '14digit', normalised: trimmed, status: 'valid' };
  }
  if (ABHA_ADDRESS_RE.test(trimmed)) {
    return { raw: input, shape: 'address', normalised: trimmed.toLowerCase(), status: 'valid' };
  }
  if (digits.length === 14) {
    return { raw: input, shape: '14digit', normalised: digits, status: 'structural' };
  }
  return { raw: input, shape: '14digit', normalised: input, status: 'invalid' };
}

export function makeAbdmPhrStub(mode: ConnectorMode): AbdmPhrStubAdapter {
  return new AbdmPhrStubAdapter(mode);
}

// Stamp the build to record where live wiring lives.
export const ABDM_STUB_BUILD_NOTES = Object.freeze({
  validatorOnly: true,
  liveAt: 'fork-9 health connectors',
  schema: 'FHIR R4 via @hl7/fhir',
  consentArtefact: 'ABDM HIE-CM consent receipt v1',
  surfacedAt: nowIso(),
});
